import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { volassisAPI } from '../services/api';
import toast from 'react-hot-toast';
import PendingHoursList from './PendingHoursList';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AdminPanel() {
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [inactiveOrganizations, setInactiveOrganizations] = useState([]);
  const [error, setError] = useState('');

  // Dialog states
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'add', user: null });
  const [orgDialog, setOrgDialog] = useState({ open: false, mode: 'add', organization: null });

  // Form states
  const [userForm, setUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    active: true,
    isAdmin: false,
  });

  const [orgForm, setOrgForm] = useState({
    orgId: '',
    name: '',
    category: '',
    email: '',
    phone: '',
    address: '',
    active: true,
  });

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [pendingHours, setPendingHours] = useState([]);

  useEffect(() => {
    if (currentUser?.id) {
      loadAdminData();
    }
  }, [currentUser]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load users
      const [activeUsersRes, inactiveUsersRes] = await Promise.all([
        volassisAPI.getActiveUsers(),
        volassisAPI.getInactiveUsers(),
      ]);

      if (activeUsersRes && activeUsersRes.output) {
        setActiveUsers(activeUsersRes.output);
      }
      if (inactiveUsersRes && inactiveUsersRes.output) {
        setInactiveUsers(inactiveUsersRes.output);
      }

      // Load organizations
      const [activeOrgsRes, inactiveOrgsRes] = await Promise.all([
        volassisAPI.getOrganizations(),
        volassisAPI.getInactiveOrganizations(),
      ]);

      if (activeOrgsRes && activeOrgsRes.output) {
        setActiveOrganizations(activeOrgsRes.output);
      }
      if (inactiveOrgsRes && inactiveOrgsRes.output) {
        setInactiveOrganizations(inactiveOrgsRes.output);
      }

      setUsers([...activeUsers, ...inactiveUsers]);
      setOrganizations([...activeOrganizations, ...inactiveOrganizations]);

    } catch (error) {
      console.error('Error loading admin data:', error);
      setError('Failed to load administrative data');
      toast.error('Failed to load administrative data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingHours = async () => {
    // fetch logic here
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // User Management Functions
  const openUserDialog = (mode, user = null) => {
    if (mode === 'edit' && user) {
      setUserForm({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        password: '',
        phone: user.phone || '',
        address: user.address || '',
        active: user.active || false,
        isAdmin: user.is_admin || false,
      });
    } else {
      setUserForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        active: true,
        isAdmin: false,
      });
    }
    setUserDialog({ open: true, mode, user });
  };

  const closeUserDialog = () => {
    setUserDialog({ open: false, mode: 'add', user: null });
    setUserForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      address: '',
      active: true,
      isAdmin: false,
    });
  };

  const handleUserSubmit = async () => {
    try {
      if (userDialog.mode === 'add') {
        const result = await volassisAPI.signUp(userForm);
        if (result && result.output) {
          toast.success('User created successfully!');
          closeUserDialog();
          loadAdminData();
        } else {
          toast.error('Failed to create user');
        }
      } else {
        const result = await volassisAPI.updateUser({
          id: userDialog.user.id,
          ...userForm,
        });
        if (result && result.output) {
          toast.success('User updated successfully!');
          closeUserDialog();
          loadAdminData();
        } else {
          toast.error('Failed to update user');
        }
      }
    } catch (error) {
      console.error('User operation error:', error);
      toast.error('An error occurred during user operation');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const result = await volassisAPI.deleteUser(userId);
        if (result && result.output) {
          toast.success('User deleted successfully!');
          loadAdminData();
        } else {
          toast.error('Failed to delete user');
        }
      } catch (error) {
        console.error('Delete user error:', error);
        toast.error('An error occurred while deleting user');
      }
    }
  };

  // Organization Management Functions
  const openOrgDialog = (mode, organization = null) => {
    if (mode === 'edit' && organization) {
      setOrgForm({
        orgId: organization.org_id || '',
        name: organization.name || '',
        category: organization.category || '',
        email: organization.email || '',
        phone: organization.phone || '',
        address: organization.address || '',
        active: organization.active || false,
      });
    } else {
      setOrgForm({
        orgId: '',
        name: '',
        category: '',
        email: '',
        phone: '',
        address: '',
        active: true,
      });
    }
    setOrgDialog({ open: true, mode, organization });
  };

  const closeOrgDialog = () => {
    setOrgDialog({ open: false, mode: 'add', organization: null });
    setOrgForm({
      orgId: '',
      name: '',
      category: '',
      email: '',
      phone: '',
      address: '',
      active: true,
    });
  };

  const handleOrgSubmit = async () => {
    try {
      if (orgDialog.mode === 'add') {
        const result = await volassisAPI.addOrganization(orgForm);
        if (result && result.output) {
          toast.success('Organization created successfully!');
          closeOrgDialog();
          loadAdminData();
        } else {
          toast.error('Failed to create organization');
        }
      } else {
        const result = await volassisAPI.updateOrganization(orgForm);
        if (result && result.output) {
          toast.success('Organization updated successfully!');
          closeOrgDialog();
          loadAdminData();
        } else {
          toast.error('Failed to update organization');
        }
      }
    } catch (error) {
      console.error('Organization operation error:', error);
      toast.error('An error occurred during organization operation');
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        const result = await volassisAPI.deleteOrganization(orgId);
        if (result && result.output) {
          toast.success('Organization deleted successfully!');
          loadAdminData();
        } else {
          toast.error('Failed to delete organization');
        }
      } catch (error) {
        console.error('Delete organization error:', error);
        toast.error('An error occurred while deleting organization');
      }
    }
  };

  const getStatusColor = (status) => {
    return status ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Administrative Panel
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
            <Tab label="User Management" icon={<PersonIcon />} />
            <Tab label="Organization Management" icon={<BusinessIcon />} />
            <Tab label="System Overview" icon={<SettingsIcon />} />
          </Tabs>
        </Box>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">User Management</Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openUserDialog('add')}
                sx={{ mr: 1 }}
              >
                Add User
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAdminData}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Active Users ({activeUsers.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.is_admin ? 'Admin' : 'Volunteer'}
                                color={user.is_admin ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => openUserDialog('edit', user)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Inactive Users ({inactiveUsers.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inactiveUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.is_admin ? 'Admin' : 'Volunteer'}
                                color={user.is_admin ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => openUserDialog('edit', user)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Organization Management Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Organization Management</Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openOrgDialog('add')}
                sx={{ mr: 1 }}
              >
                Add Organization
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAdminData}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Active Organizations ({activeOrganizations.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeOrganizations.map((org) => (
                          <TableRow key={org.org_id}>
                            <TableCell>{org.name}</TableCell>
                            <TableCell>{org.category}</TableCell>
                            <TableCell>
                              <Chip
                                label="Active"
                                color="success"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => openOrgDialog('edit', org)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteOrg(org.org_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Inactive Organizations ({inactiveOrganizations.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inactiveOrganizations.map((org) => (
                          <TableRow key={org.org_id}>
                            <TableCell>{org.name}</TableCell>
                            <TableCell>{org.category}</TableCell>
                            <TableCell>
                              <Chip
                                label="Inactive"
                                color="error"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => openOrgDialog('edit', org)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteOrg(org.org_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* System Overview Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            System Overview
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Statistics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Total Users"
                        secondary={users.length}
                      />
                      <ListItemSecondaryAction>
                        <PersonIcon color="primary" />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Active Users"
                        secondary={activeUsers.length}
                      />
                      <ListItemSecondaryAction>
                        <CheckIcon color="success" />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Inactive Users"
                        secondary={inactiveUsers.length}
                      />
                      <ListItemSecondaryAction>
                        <CancelIcon color="error" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Organization Statistics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Total Organizations"
                        secondary={organizations.length}
                      />
                      <ListItemSecondaryAction>
                        <BusinessIcon color="primary" />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Active Organizations"
                        secondary={activeOrganizations.length}
                      />
                      <ListItemSecondaryAction>
                        <CheckIcon color="success" />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Inactive Organizations"
                        secondary={inactiveOrganizations.length}
                      />
                      <ListItemSecondaryAction>
                        <CancelIcon color="error" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onClose={closeUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userDialog.mode === 'add' ? 'Add New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userForm.firstName}
                onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userForm.lastName}
                onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                helperText={userDialog.mode === 'edit' ? 'Leave blank to keep current password' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                value={userForm.address}
                onChange={(e) => setUserForm({ ...userForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={userForm.active}
                    onChange={(e) => setUserForm({ ...userForm, active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={userForm.isAdmin}
                    onChange={(e) => setUserForm({ ...userForm, isAdmin: e.target.checked })}
                  />
                }
                label="Admin Role"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUserDialog}>Cancel</Button>
          <Button onClick={handleUserSubmit} variant="contained">
            {userDialog.mode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Organization Dialog */}
      <Dialog open={orgDialog.open} onClose={closeOrgDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {orgDialog.mode === 'add' ? 'Add New Organization' : 'Edit Organization'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Organization ID"
                value={orgForm.orgId}
                onChange={(e) => setOrgForm({ ...orgForm, orgId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={orgForm.name}
                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={orgForm.category}
                onChange={(e) => setOrgForm({ ...orgForm, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={orgForm.email}
                onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={orgForm.phone}
                onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                value={orgForm.address}
                onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={orgForm.active}
                    onChange={(e) => setOrgForm({ ...orgForm, active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOrgDialog}>Cancel</Button>
          <Button onClick={handleOrgSubmit} variant="contained">
            {orgDialog.mode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending Hours List */}
      <PendingHoursList
        orgId={selectedOrgId}
        pendingHours={pendingHours}
        refresh={fetchPendingHours}
      />
    </Box>
  );
}

export default AdminPanel;
